import { _ } from "../polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import InlineListingWidget from "./InlineListingWidget.js";

/**
 * Render an inline list of a dimension specification. This only works
 * if the specification is editable (see
 * `DimensionSpecification.canBeEdited()`).
 *
 * @param {dimension} spec.dimensionTree - A dimension tree from which
 * to intrepret the dimension specification.
 * @param {dimensionSpecification} spec.dimensionSpecification - The
 * dimension specification to render.
 * @param {Renderable} spec.prefix - Something to render before the first dimension.
 */
export default class InlineDimensionLabelsWidget extends Widget {
	constructor({ dimensionTree, dimensionSpecification, prefix }) {
		super(...arguments);

		this._dimensionTree = dimensionTree;
		this._dimensionSpecification = dimensionSpecification;
		this._prefix = prefix;

		this._inlineListingWidget = new InlineListingWidget({
			visibleElementCssClass: "tag",
			separator: "",
			elements: this._dimensionRenderers(),
		});
	}

	renderContentOn(html) {
		if (!this._dimensionSpecification) {
			return;
		}

		html.div(
			{ klass: "inline-dimension-labels" },
			this._prefix,
			this._inlineListingWidget,
		);
	}

	_includedDimensions() {
		return this._dimensionTree.includedDimensions(
			this._dimensionSpecification,
		);
	}

	_excludedDimensions() {
		return this._dimensionTree.excludedDimensions(
			this._dimensionSpecification,
		);
	}

	_dimensionRenderers() {
		const includedDimensionRenderers = this._includedDimensions().map(
			(dimension) =>
				this._dimensionRenderer(dimension, dimension.getLabel()),
		);

		const excludedDimensionRenderers = this._excludedDimensions().map(
			(dimension) =>
				this._dimensionRenderer(
					dimension,
					_("excluding {label}", { label: dimension.getLabel() }),
				),
		);

		return [...includedDimensionRenderers, ...excludedDimensionRenderers];
	}

	_dimensionRenderer(dimension, label) {
		return (html) => {
			const span = html.span(
				{
					title: this._dimensionTree.pathNameToChildId(
						dimension.getId(),
					),
					"data-toggle": "tooltip",
					"data-placement": "top",
				},
				label,
			);

			span.asJQuery().tooltip();
		};
	}
}
